import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './PreferencesSection.scss';
import Phone from './Phone';
import OptOutInfo from './OptOutInfo';
import TermsOfUseLink from 'common/TermsOfUseLink';
import PrivacyPolicyLink from 'common/PrivacyPolicyLink';
import Checkbox from 'SF/Checkbox';
import Icon from 'SF/MaterialIcon';
import { isEmptyString } from 'utils/text';
import { getWord } from 'utils/tenant';
import { pressedEnterOrSpace } from 'utils/accessibility';
import { isSupportedCellPhoneNumber, isSupportedCountryCode } from 'utils/profile';

/**
 * @returns {node} the component for users to configure their mobile phone number and their opt-in preference for
 * receiving notifications via text. The opt-in selection only appears when text messaging is enabled.
 */
const CellPhone = ({
    cellPhone, goToNotificationsTab, setValue, showNotificationsLinks, textMessagingEnabled,
    textNotificationEnabledForUser, supportedCountries, isStaff
}) => {
    const isValidTextingCountry = isSupportedCellPhoneNumber(cellPhone, supportedCountries);

    const getOptIn = () => {
        if (!textMessagingEnabled) return null;
        return (
            <div className={classNames({[css.error]: !isValidTextingCountry})}>
                <span className={css['info-message']} aria-hidden={isValidTextingCountry}>
                    <Icon icon="error" aria-hidden="true" />The current country code is not supported for texting.
                </span>
                <div className={css['profile-adv-check']}>
                    <Checkbox ariaLabel=""
                              aria-labelledby="mobileOptIn"
                              checked={isValidTextingCountry && textNotificationEnabledForUser}
                              disabled={isEmptyString(cellPhone) || !isValidTextingCountry}
                              onChange={(checked) => setValue({ notificationPreferences: {textNotificationEnabled: checked}})} />
                    <span id="mobileOptIn" className={css['profile-adv-options'] + ' ' + css['profile-sub-text']}>
                    Send text notifications to my {getWord('mobilePhone', true)}.
                    <p>
                        You may receive texts about {getWord('Starfish')} activity, such as appointment reminders and other updates, depending on your institution’s settings.
                        Message and data rates may apply. Frequency varies. See <TermsOfUseLink /> and <PrivacyPolicyLink />.
                    </p>
                    <OptOutInfo cellPhone={cellPhone}
                                textNotificationEnabledForUser={textNotificationEnabledForUser}
                                isStaff={isStaff} />
                        { showNotificationsLinks &&
                        <div>
                            <a href='#'
                               onClick={goToNotificationsTab}
                               onKeyDown={(e) => { if (pressedEnterOrSpace(e)) goToNotificationsTab() }}>
                                Edit Notification Preferences
                            </a>
                        </div>
                        }
                    </span>
                </div>
            </div>
        )
    };

    return (
        <div id="mobile-phone-section">
            <label id="mobilePhone">{getWord('mobilePhone')}</label>
            <Phone labelledById="mobilePhone"
                   onChange={updatedPhone => {
                       setValue(
                           isEmptyString(updatedPhone) ?
                               {
                                   userProfile: {cellPhone: ''},
                                   notificationPreferences: {textNotificationEnabled: false}
                               } : {userProfile: {cellPhone: updatedPhone}})
                   }}
                   validateCountry={(country) => isSupportedCountryCode(country, supportedCountries, cellPhone)}
                   value={cellPhone} />
            { getOptIn() }
        </div>
    )
};

CellPhone.propTypes = {
    cellPhone: PropTypes.string,
    goToNotificationsTab: PropTypes.func,
    setValue: PropTypes.func,
    showNotificationsLinks: PropTypes.bool,
    textMessagingEnabled: PropTypes.bool,
    textNotificationEnabledForUser: PropTypes.bool,
    supportedCountries: PropTypes.string,
    isStaff: PropTypes.bool
};

export default CellPhone;
